import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  TextField,
} from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { TProjectFormData } from 'src/@types';
import { IClient } from 'src/@types/apiResponseTypes';
import { clientApi } from 'src/api';
import DialogTitleWithCloseButton from 'src/components/dialog/DialogTitleWithCloseButton';

import ClientTable from '../../client-management/sections/ClientTable';

interface RHFClientSelectProps {
  disabled?: boolean;
  readOnly?: boolean;
  name: keyof TProjectFormData;
  label: string;
}

function RHFClientSelect({ disabled, name, label, readOnly }: RHFClientSelectProps) {
  const {
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<TProjectFormData>();
  const { enqueueSnackbar } = useSnackbar();
  const [clientSelectModalOpen, setClientSelectModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<IClient | null>(null);
  // selection model
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);

  useEffect(() => {
    const value = getValues(name);
    if (isEmpty(value)) {
      setSelectedClient(null);
      return;
    }

    const client = value as IClient;
    setSelectedClient(client);
    setSelectionModel([client.customerSid]);
  }, [clientSelectModalOpen, name, getValues]);

  // 고객사 선택 모달 열기
  const handleOpen = () => {
    if (readOnly || disabled) {
      return;
    }
    setClientSelectModalOpen(true);
  };

  // 고객사 선택 모달 닫기
  const handleClose = () => {
    setClientSelectModalOpen(false);
  };

  // 고객사 선택 모달에서 선택한 고객사 적용
  const handleApply = async () => {
    try {
      // 선택한 고객사가 없으면 리턴
      if (isEmpty(selectionModel)) {
        setValue(name, {} as IClient);
        handleClose();
        return;
      }

      const selectedCustomerSid = selectionModel[0];
      const result = await clientApi.findClient(selectedCustomerSid as number);
      if (result) {
        const client = result.data;
        setValue(name, client);
        handleClose();
        return;
      }

      enqueueSnackbar(`${label} 적용에 실패했습니다.`, { variant: 'error' });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <TextField
        label={label}
        value={selectedClient?.company || ''}
        onClick={handleOpen}
        focused={false}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        disabled={disabled}
        error={!!errors[name]}
        helperText={errors[name]?.message}
      />
      {/* 고객사 선택 모달 */}
      <Dialog fullWidth maxWidth="sm" open={clientSelectModalOpen} onClose={handleClose}>
        <DialogTitleWithCloseButton title={`${label} 조회`} onClose={handleClose} />
        <DialogContent sx={{ height: 500 }}>
          <ClientTable
            checkboxSelection
            disableMultipleRowSelection
            keepNonExistentRowsSelected
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={setSelectionModel}
            hideDivisionColumn={true}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>취소</Button>
          <Button variant="contained" onClick={handleApply}>
            적용하기
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default RHFClientSelect;
